/**
 * @file: ReplyPreviewBar.tsx
 *
 * The strip above the composer showing the message being replied to, with
 * an `×` to clear the reply target. Pure — the container owns the target.
 */

import { Typography } from '@euler/app/components/typography';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMsg } from '@euler/model/chat';
import { FC, memo } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import {
  MessagePayloadView,
  isRichPayloadType,
} from '../message/MessagePayloadView';

export const ReplyPreviewBar: FC<{
  target: ChatMsg;
  onClear: () => void;
  style?: StyleProp<ViewStyle>;
}> = memo(({ target, onClear, style }) => {
  const { rpx } = useResponsiveContext();
  const { payload } = target;
  // Rich kinds (image/video/file/card/AI) preview via their renderer's
  // compact form; plain text shows its content inline.
  const rich = isRichPayloadType(payload.type);
  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(8),
          backgroundColor: '#f3f4f6',
          borderTopWidth: StyleSheet.hairlineWidth,
          borderTopColor: '#e0e0e0',
        },
        style,
      ]}
    >
      <View
        style={{
          flex: 1,
          borderLeftWidth: rpx(3),
          borderLeftColor: '#3b82f6',
          paddingLeft: rpx(8),
        }}
      >
        <Typography.Text
          size={rpx(11)}
          color="#3b82f6"
          style={{ fontFamily: FontFamily.NotoSans.Medium }}
        >
          回复 {target.authorName ?? '消息'}
        </Typography.Text>
        {rich ? (
          // The reply preview is non-interactive — the renderer's inner
          // touchables shouldn't capture taps.
          <View style={{ marginTop: rpx(4) }} pointerEvents="none">
            <MessagePayloadView payload={payload} isMine={false} quoted />
          </View>
        ) : (
          <Typography.Text
            size={rpx(12)}
            color="#6b7280"
            numberOfLines={1}
            style={{ marginTop: rpx(2) }}
          >
            {payload.type === 'text' ? payload.text : `[${target.payloadType}]`}
          </Typography.Text>
        )}
      </View>
      <TouchableOpacity
        onPress={onClear}
        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
        style={{ marginLeft: rpx(8) }}
      >
        <Typography.Text size={rpx(16)} color="#9ca3af">
          ×
        </Typography.Text>
      </TouchableOpacity>
    </View>
  );
});
